import type { CartLine } from '../lib/cart';
import { formatPrice, lineTotalCents } from '../lib/cart';
import { serviceFeeFor, CUSTOMER_NOTE_MAX } from '../lib/checkout';

/**
 * Vue panier (B5) — présentation PARITÉ LEGACY (2026-06-24) : page « Mon panier » en
 * 2 colonnes (lignes à gauche, **Récapitulatif collant** à droite avec frais + total).
 * « Modifier » rouvre la fiche produit pré-remplie (géré par le parent MenuView).
 */
interface CartViewProps {
  lines: CartLine[];
  currency?: 'EUR' | 'CHF';
  subtotal: number;
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
  onBack: () => void;
  onCheckout: () => void;
  /** Remarque client (parité legacy) — saisie ici, sous le panier, avant le créneau. */
  note?: string;
  onNoteChange?: (v: string) => void;
}

export default function CartView({
  lines,
  currency = 'EUR',
  subtotal,
  onEdit,
  onRemove,
  onBack,
  onCheckout,
  note = '',
  onNoteChange,
}: CartViewProps) {
  const serviceFee = serviceFeeFor(lines);
  const total = subtotal + serviceFee;
  const cardClass = 'rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:p-5';

  return (
    <section className="w-full">
      <button onClick={onBack} className="mb-2 text-sm font-semibold text-slate-500 hover:text-slate-800">
        ← Continuer mes achats
      </button>
      <h1 className="display-font mb-5 text-3xl font-black sm:text-4xl">Mon panier</h1>

      {lines.length === 0 ? (
        <div className={cardClass}>
          <p className="text-sm opacity-60">Votre panier est vide.</p>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
          {/* ===== Colonne gauche — lignes ===== */}
          <div className="flex flex-col gap-3">
            {lines.map((l, i) => (
              <div key={i} className={'flex items-center gap-4 ' + cardClass}>
                <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-100 text-xl" aria-hidden="true">
                  {l.imageUrl ? (
                    <img src={l.imageUrl} alt="" loading="lazy" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    '🍽️'
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-bold">{l.name}</span>
                  <span className="mt-1 flex items-center gap-3">
                    <span className="text-xs text-slate-500">{l.quantity} × {formatPrice(l.unitPriceCents, currency)}</span>
                    {l.productId && (
                      <button onClick={() => onEdit(i)} className="text-xs font-semibold text-brand-primary hover:underline">
                        ✏️ Modifier / ajouter
                      </button>
                    )}
                  </span>
                </span>
                <span className="flex-shrink-0 font-extrabold">{formatPrice(lineTotalCents(l), currency)}</span>
                <button onClick={() => onRemove(i)} className="flex-shrink-0 text-slate-300 hover:text-red-500" title="Retirer" aria-label="Retirer">✕</button>
              </div>
            ))}

            <button
              onClick={onBack}
              className="rounded-2xl border-2 border-dashed border-slate-200 py-3.5 text-sm font-semibold text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
            >
              + Ajouter un autre article
            </button>

            {/* Remarque (parité legacy) — sous le panier, juste avant le choix du créneau. */}
            <div className={cardClass}>
              <label htmlFor="cart-note" className="text-sm font-semibold">
                Une remarque pour votre commande ? <span className="font-normal opacity-50">(facultatif)</span>
              </label>
              <textarea
                id="cart-note"
                value={note}
                maxLength={CUSTOMER_NOTE_MAX}
                rows={3}
                onChange={(e) => onNoteChange?.(e.target.value)}
                placeholder="Ex. : sans oignon, sonner à l'interphone…"
                className="mt-1 w-full rounded-lg border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2"
                style={{ ['--tw-ring-color' as string]: 'var(--brand-primary)' }}
              />
              <span className="block text-right text-xs opacity-50">
                {note.length}/{CUSTOMER_NOTE_MAX}
              </span>
            </div>
          </div>

          {/* ===== Colonne droite — Récapitulatif collant ===== */}
          <aside className="h-fit lg:sticky lg:top-24">
            <div className={cardClass}>
              <h3 className="mb-3 text-lg font-extrabold">Récapitulatif</h3>
              <div className="flex flex-col gap-1.5 text-sm">
                <div className="flex items-center justify-between text-slate-600">
                  <span>Sous-total</span>
                  <span>{formatPrice(subtotal, currency)}</span>
                </div>
                <div className="flex items-center justify-between text-slate-600">
                  <span>Frais de commande</span>
                  <span>{formatPrice(serviceFee, currency)}</span>
                </div>
                <div className="my-2 border-t border-slate-100" />
                <div className="flex items-center justify-between text-lg font-extrabold">
                  <span>Total</span>
                  <span style={{ color: 'var(--brand-primary)' }}>{formatPrice(total, currency)}</span>
                </div>
              </div>
              <button
                onClick={onCheckout}
                className="mt-4 w-full rounded-xl py-3.5 text-base font-bold text-white shadow-lg transition active:scale-95"
                style={{ background: 'var(--brand-primary)' }}
              >
                Choisir mon créneau →
              </button>
              <p className="mt-3 text-center text-[11px] text-slate-400">
                🔒 Paiement sécurisé · Aucun frais caché · 0 % commission au resto
              </p>
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}
